import { useState, useEffect } from 'react';
import adminService from '../../services/admin';
import toast from 'react-hot-toast';

function PlanManagement() {
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  useEffect(() => {
    loadPlans();
  }, [includeInactive]);

  const loadPlans = async () => {
    setIsLoading(true);
    try {
      const data = await adminService.getPlans(includeInactive);
      setPlans(data.plans);
    } catch (error) {
      toast.error('Failed to load plans');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPlan(null);
    setShowModal(true);
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setShowModal(true);
  };

  const handleDelete = async (planId) => {
    if (!confirm('Are you sure you want to delete this plan?')) return;
    
    try {
      await adminService.deletePlan(planId);
      toast.success('Plan deleted');
      loadPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete plan');
    }
  };

  const handleSetDefault = async (planId) => {
    try {
      await adminService.setDefaultPlan(planId);
      toast.success('Default plan updated');
      loadPlans();
    } catch (error) {
      toast.error('Failed to set default plan');
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingPlan) {
        await adminService.updatePlan(editingPlan.id, data);
        toast.success('Plan updated');
      } else {
        await adminService.createPlan(data);
        toast.success('Plan created');
      }
      setShowModal(false);
      loadPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save plan');
    }
  };

  const handleSeedPlans = async () => {
    try {
      const result = await adminService.seedPlans();
      toast.success(result.message);
      loadPlans();
    } catch (error) {
      toast.error('Failed to seed plans');
    }
  };

  if (isLoading && plans.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <span className="spinner w-12 h-12"></span>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Plan Management</h1>
        <div className="flex gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-400">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Show inactive
          </label>
          {plans.length === 0 && (
            <button onClick={handleSeedPlans} className="btn btn-secondary">
              Seed Default Plans
            </button>
          )}
          <button onClick={handleCreate} className="btn btn-primary">
            + Add Plan
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`card ${!plan.is_active ? 'opacity-60' : ''} ${
              plan.is_default ? 'border-purple-500' : ''
            }`}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold">{plan.name}</h3>
                <p className="text-gray-400 text-sm">{plan.slug}</p>
              </div>
              <div className="flex gap-1">
                {plan.is_default && (
                  <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs rounded">
                    Default
                  </span>
                )}
                {!plan.is_active && (
                  <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded">
                    Inactive
                  </span>
                )}
              </div>
            </div>

            <p className="text-3xl font-bold mb-4">
              ${plan.price_monthly}
              <span className="text-gray-400 text-base font-normal">/month</span>
            </p>

            <div className="space-y-2 mb-4 text-sm text-gray-400">
              <p>Daily: {plan.daily_limit} | Monthly: {plan.monthly_limit}</p>
              <p>Max chars: {plan.max_characters.toLocaleString()}</p>
              <p>Workspace seats: {plan.workspace_seats || 1}</p>
              <p>
                Voice Cloning: {plan.voice_cloning_enabled ? `Yes (${plan.max_cloned_voices})` : 'No'}
              </p>
              <p>API Access: {plan.api_access_enabled ? 'Yes' : 'No'}</p>
            </div>

            {plan.features && plan.features.length > 0 && (
              <ul className="space-y-1 mb-4 text-sm">
                {plan.features.slice(0, 4).map((feature, idx) => (
                  <li key={idx} className="flex items-start text-gray-400">
                    <span className="text-green-400 mr-2">✓</span>
                    {feature}
                  </li>
                ))}
                {plan.features.length > 4 && (
                  <li className="text-gray-500">+{plan.features.length - 4} more</li>
                )}
              </ul>
            )}

            <div className="flex gap-2 pt-4 border-t border-gray-700">
              <button
                onClick={() => handleEdit(plan)}
                className="btn btn-secondary btn-sm flex-1"
              >
                Edit
              </button>
              {!plan.is_default && (
                <>
                  <button
                    onClick={() => handleSetDefault(plan.id)}
                    className="btn btn-secondary btn-sm"
                  >
                    Set Default
                  </button>
                  <button
                    onClick={() => handleDelete(plan.id)}
                    className="text-red-400 hover:text-red-300 px-2"
                  >
                    🗑
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <PlanModal
          plan={editingPlan}
          onSave={handleSave}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

function PlanModal({ plan, onSave, onClose }) {
  const [formData, setFormData] = useState({
    name: plan?.name || '',
    slug: plan?.slug || '',
    description: plan?.description || '',
    price_monthly: plan?.price_monthly || 0,
    price_yearly: plan?.price_yearly || 0,
    daily_limit: plan?.daily_limit || 10,
    monthly_limit: plan?.monthly_limit || 100,
    max_characters: plan?.max_characters || 500,
    workspace_seats: plan?.workspace_seats || 1,
    voice_cloning_enabled: plan?.voice_cloning_enabled || false,
    max_cloned_voices: plan?.max_cloned_voices || 0,
    premium_voices_enabled: plan?.premium_voices_enabled || false,
    api_access_enabled: plan?.api_access_enabled || false,
    priority_processing: plan?.priority_processing || false,
    features: plan?.features?.join('\n') || '',
    is_active: plan?.is_active ?? true,
    sort_order: plan?.sort_order || 0,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const data = {
      ...formData,
      features: formData.features.split('\n').filter((f) => f.trim()),
    };
    
    onSave(data);
  };

  const handleSlugify = () => {
    const slug = formData.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    setFormData({ ...formData, slug });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-auto py-8">
      <div className="card w-full max-w-2xl max-h-[90vh] overflow-auto">
        <h2 className="text-xl font-semibold mb-4">
          {plan ? 'Edit Plan' : 'Create Plan'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                onBlur={!plan ? handleSlugify : undefined}
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Slug *</label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                className="input w-full"
                pattern="^[a-z0-9-]+$"
                required
                disabled={!!plan}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input w-full"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Monthly Price ($)</label>
              <input
                type="number"
                step="0.01"
                value={formData.price_monthly}
                onChange={(e) =>
                  setFormData({ ...formData, price_monthly: parseFloat(e.target.value) })
                }
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Yearly Price ($)</label>
              <input
                type="number"
                step="0.01"
                value={formData.price_yearly}
                onChange={(e) =>
                  setFormData({ ...formData, price_yearly: parseFloat(e.target.value) })
                }
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Sort Order</label>
              <input
                type="number"
                value={formData.sort_order}
                onChange={(e) =>
                  setFormData({ ...formData, sort_order: parseInt(e.target.value) })
                }
                className="input w-full"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Daily Limit</label>
              <input
                type="number"
                value={formData.daily_limit}
                onChange={(e) =>
                  setFormData({ ...formData, daily_limit: parseInt(e.target.value) })
                }
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Monthly Limit</label>
              <input
                type="number"
                value={formData.monthly_limit}
                onChange={(e) =>
                  setFormData({ ...formData, monthly_limit: parseInt(e.target.value) })
                }
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Max Characters</label>
              <input
                type="number"
                value={formData.max_characters}
                onChange={(e) =>
                  setFormData({ ...formData, max_characters: parseInt(e.target.value) })
                }
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Workspace Seats</label>
              <input
                type="number"
                min="1"
                value={formData.workspace_seats}
                onChange={(e) =>
                  setFormData({ ...formData, workspace_seats: parseInt(e.target.value, 10) || 1 })
                }
                className="input w-full"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.voice_cloning_enabled}
                  onChange={(e) =>
                    setFormData({ ...formData, voice_cloning_enabled: e.target.checked })
                  }
                />
                <span className="text-sm">Voice Cloning Enabled</span>
              </label>
              {formData.voice_cloning_enabled && (
                <input
                  type="number"
                  placeholder="Max cloned voices"
                  value={formData.max_cloned_voices}
                  onChange={(e) =>
                    setFormData({ ...formData, max_cloned_voices: parseInt(e.target.value) })
                  }
                  className="input w-full"
                />
              )}
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.premium_voices_enabled}
                  onChange={(e) =>
                    setFormData({ ...formData, premium_voices_enabled: e.target.checked })
                  }
                />
                <span className="text-sm">Premium Voices</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.api_access_enabled}
                  onChange={(e) =>
                    setFormData({ ...formData, api_access_enabled: e.target.checked })
                  }
                />
                <span className="text-sm">API Access</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.priority_processing}
                  onChange={(e) =>
                    setFormData({ ...formData, priority_processing: e.target.checked })
                  }
                />
                <span className="text-sm">Priority Processing</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">
              Features (one per line)
            </label>
            <textarea
              value={formData.features}
              onChange={(e) => setFormData({ ...formData, features: e.target.value })}
              className="input w-full"
              rows={4}
              placeholder="Unlimited TTS conversions&#10;Premium voices&#10;Priority support"
            />
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
            />
            <span className="text-sm">Active</span>
          </label>

          <div className="flex gap-2 pt-4">
            <button type="submit" className="btn btn-primary flex-1">
              {plan ? 'Update' : 'Create'}
            </button>
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PlanManagement;
